/**
 * Display copy for a Bill Payment PO's QuickBooks vendor-bill link.
 * Pure — UI on Purchasing and Billing & Payables.
 */

import type { PurchaseQbBillStatus } from '../types/purchasing';

export type PurchaseQbBillView = {
  status: PurchaseQbBillStatus;
  title: string;
  /** One-line AP chip — keep the row height. */
  chip: string;
  detail: string;
  url?: string;
  tone: 'ok' | 'warn' | 'muted';
};

function moneyLabel(amount: number | null | undefined): string {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function qboVendorBillAppUrl(billId: string): string {
  const id = billId.trim();
  return id ? `https://app.qbo.intuit.com/app/bill?txnId=${encodeURIComponent(id)}` : '';
}

export function isPurchaseQbBillStatus(value: unknown): value is PurchaseQbBillStatus {
  return (
    value === 'linked'
    || value === 'needs_vendor'
    || value === 'needs_bill'
    || value === 'pending'
    || value === 'skipped'
  );
}

export function describePurchaseQbBill(input: {
  status?: PurchaseQbBillStatus | string | null;
  url?: string | null;
  message?: string | null;
  vendorName?: string | null;
  amount?: number | null;
  invoiceNumber?: string | null;
}): PurchaseQbBillView | null {
  if (!isPurchaseQbBillStatus(input.status) || input.status === 'skipped') return null;
  const vendor = (input.vendorName || '').trim() || 'this vendor';
  const invoice = (input.invoiceNumber || '').trim();
  const amount = moneyLabel(input.amount);
  const invoiceBit = invoice ? ` invoice ${invoice}` : '';
  const amountBit = amount ? ` (${amount})` : '';
  const custom = (input.message || '').trim();
  const url = (input.url || '').trim() || undefined;

  if (input.status === 'linked') {
    return {
      status: 'linked',
      title: 'QuickBooks bill linked',
      chip: 'QB linked',
      detail: custom || `Open the vendor bill in QuickBooks${invoiceBit}.`,
      url,
      tone: 'ok',
    };
  }
  if (input.status === 'pending') {
    return {
      status: 'pending',
      title: 'QuickBooks bill pending',
      chip: 'QB pending',
      detail: custom || 'Trying to create the vendor bill in QuickBooks.',
      tone: 'muted',
    };
  }
  if (input.status === 'needs_vendor') {
    return {
      status: 'needs_vendor',
      title: 'Office must create the vendor and bill in QuickBooks',
      chip: 'QB vendor needed',
      detail:
        custom
        || `${vendor} is not a QuickBooks vendor yet. Create the vendor, then enter bill${invoiceBit}${amountBit}.`,
      tone: 'warn',
    };
  }
  return {
    status: 'needs_bill',
    title: 'Not in QuickBooks yet',
    chip: 'Not in QB',
    detail:
      custom
      || `The PO is issued, but QuickBooks does not have a linked bill yet. Enter a vendor bill for ${vendor}${invoiceBit}${amountBit}.`,
    tone: 'warn',
  };
}

export type QboAccountLite = {
  qbId: string;
  name: string;
  fullyQualifiedName?: string;
  accountType?: string;
  active?: boolean;
};

export type QboVendorLite = {
  Id: string;
  DisplayName?: string;
  CompanyName?: string;
  Active?: boolean;
};

/** Designer-invoice bills always post to Design — never Professional Services / 1099 / etc. */
export function designAccountLeafName(row: {
  name?: string;
  fullyQualifiedName?: string;
  qbAccountName?: string;
}): string {
  const raw = (row.fullyQualifiedName || row.qbAccountName || row.name || '').trim();
  const parts = raw.split(':');
  return (parts[parts.length - 1] || '').trim().toLowerCase();
}

export function isForcedDesignAccount(row: QboAccountLite): boolean {
  if (row.active === false) return false;
  return designAccountLeafName(row) === 'design';
}

export function pickDesignerInvoiceQboExpenseAccount(input: {
  chartAccounts: QboAccountLite[];
  legendAccounts?: Array<{ qbAccountId: string; qbAccountName?: string }>;
}):
  | { ok: true; account: { qbId: string; name: string } }
  | { ok: false; reason: 'none' | 'ambiguous'; matches: string[] } {
  const legend = (input.legendAccounts || []).filter(
    (row) => row.qbAccountId?.trim() && designAccountLeafName(row) === 'design',
  );
  if (legend.length) {
    const jobCost = legend.find((row) => /job\s*cost/i.test(row.qbAccountName || ''));
    const pick = jobCost || legend[0];
    return {
      ok: true,
      account: { qbId: pick.qbAccountId.trim(), name: (pick.qbAccountName || 'Design').trim() },
    };
  }

  const hits = input.chartAccounts.filter(isForcedDesignAccount);
  if (!hits.length) return { ok: false, reason: 'none', matches: [] };
  const jobCost = hits.find((row) => /job\s*cost/i.test(`${row.fullyQualifiedName || ''} ${row.name}`));
  const chosen = jobCost || hits[0];
  return {
    ok: true,
    account: { qbId: chosen.qbId, name: (chosen.fullyQualifiedName || chosen.name).trim() },
  };
}

/** @deprecated Use pickDesignerInvoiceQboExpenseAccount — designer bills force Design. */
export function pickUniqueQboExpenseAccount(
  accounts: QboAccountLite[],
):
  | { ok: true; account: { qbId: string; name: string } }
  | { ok: false; reason: 'none' | 'ambiguous'; matches: string[] } {
  return pickDesignerInvoiceQboExpenseAccount({ chartAccounts: accounts });
}

export function normalizeQboVendorName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[.,']/g, ' ')
    .replace(/\b(llc|inc|incorporated|ltd|co|company|pc|pllc)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function pickUniqueQboVendor(
  vendors: QboVendorLite[],
  wantedNames: string[],
):
  | { ok: true; vendor: { id: string; name: string } }
  | { ok: false; reason: 'none' | 'ambiguous' } {
  const wanted = wantedNames
    .map((n) => n.trim())
    .filter(Boolean);
  if (!wanted.length) return { ok: false, reason: 'none' };
  const active = vendors.filter((v) => v.Active !== false && v.Id);
  const exact = new Set(wanted.map((n) => n.toLowerCase()));
  const normalizedWanted = new Set(wanted.map(normalizeQboVendorName).filter(Boolean));

  const exactHits = active.filter((v) => {
    const display = (v.DisplayName || '').trim().toLowerCase();
    const company = (v.CompanyName || '').trim().toLowerCase();
    return exact.has(display) || (company && exact.has(company));
  });
  if (exactHits.length === 1) {
    return {
      ok: true,
      vendor: { id: exactHits[0].Id, name: (exactHits[0].DisplayName || exactHits[0].CompanyName || '').trim() },
    };
  }
  if (exactHits.length > 1) return { ok: false, reason: 'ambiguous' };

  const fuzzyHits = active.filter((v) => {
    const display = normalizeQboVendorName(v.DisplayName || '');
    const company = normalizeQboVendorName(v.CompanyName || '');
    return (display && normalizedWanted.has(display)) || (company && normalizedWanted.has(company));
  });
  if (fuzzyHits.length === 1) {
    return {
      ok: true,
      vendor: { id: fuzzyHits[0].Id, name: (fuzzyHits[0].DisplayName || fuzzyHits[0].CompanyName || '').trim() },
    };
  }
  if (fuzzyHits.length > 1) return { ok: false, reason: 'ambiguous' };
  return { ok: false, reason: 'none' };
}
